import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FEATURE_LABELS,
  PLANS,
  PLAN_ORDER,
  formatPlanPrice,
  isBillingPeriod,
  limitFor,
  planMove,
  type BillingPeriod,
  type Feature,
  type PlanCode,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { pluralise } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { useModuleLink } from "@/lib/module-rail";
import { useGating } from "@/lib/gating";
import { MeterBar } from "@/components/chart";
import { IconLock } from "@/components/icons";
import { Refusal } from "@/components/padlock";
import { Alert, Button, Card, CardTitle, ErrorState, Modal, PageHeader, Spinner } from "@/components/ui";

/**
 * Plan and billing.
 *
 * ## The one thing to understand before changing anything here
 *
 * **A plan is not a setting on this screen. It is a purchase, and it moves when the money does.**
 * ADR-0129 split what used to be one button into three different things, and the whole of this
 * file is the consequence:
 *
 * - **Moving up is a payment.** `POST /billing/checkout` opens a real charge and hands the payer
 *   to a provider; the plan moves inside `settleCharge` when the payment is proven, and never
 *   before. Nothing on this screen may act as though starting a payment were the same as making
 *   one — the customer comes back here afterwards with `?ref=` and *that* is where the answer is.
 * - **Moving down is free and immediate**, through `POST /settings/billing/plan`. An agency that
 *   wants to spend less has to be able to say so and be believed. It is asked to confirm, because
 *   the consequences are real, and the confirmation says what they are.
 * - **Enterprise is arranged, not bought.** It carries no price, so it gets a way to talk to us
 *   rather than a button that both endpoints would refuse.
 *
 * The screen this replaces posted **every** plan button at `/settings/billing/plan` and told the
 * reader "nothing is charged". Since ADR-0129 that endpoint answers `402` to an upgrade, so a
 * customer pressing "Switch to Growth" met a correct sentence drawn as a red failure — the product
 * refusing to sell itself.
 *
 * ## What decides which of the three a button is
 *
 * `planMove` from `packages/core/src/plans.ts`, and nothing else. **No comparison of plan codes
 * happens in this file**: the ladder is defined in exactly one place, Enterprise's quote-only
 * price sorts to the bottom of any numeric comparison, and a screen that worked the direction out
 * for itself would eventually disagree with the endpoint it is about to call. The same function
 * the API asks is the function this asks.
 *
 * ## Who is offered any of it
 *
 * `org.billing`, which an administrator deliberately does not hold — and per ADR-0114 a permission
 * is **hidden, never padlocked**: no amount of money changes what a role may do, so a padlock over
 * it would be a door that never opens for that person. The route itself is guarded (`Require` in
 * `App.tsx`), so the only reader who reaches this screen without the permission is a suspended
 * owner, read-only. They see their plan and their usage; they are offered nothing to press.
 */

interface BillingResponse {
  subscription: {
    plan: string;
    status: string;
    period: string;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
  } | null;
  plan: { code: PlanCode; status: string; trialDaysLeft: number | null };
  usage: {
    units: { used: number; limit: number; nearLimit: boolean };
    messages: { used: number; limit: number; nearLimit: boolean };
  };
}

/** A provider this deployment can actually take money through. Absent means it cannot. */
interface PlatformGateway {
  provider: string;
  name: string;
}

/** What `POST /billing/checkout` answers with. The plan has **not** moved at this point. */
interface CheckoutStarted {
  reference: string;
  redirectUrl: string | null;
  /** True only where no provider keys are configured, so nothing was really charged. */
  simulated: boolean;
}

/** What the public charge endpoint says about the payment somebody has just come back from. */
interface ChargeState {
  reference: string;
  status: "pending" | "paid" | "failed" | "abandoned" | "refunded";
  simulated: boolean;
}

export function BillingPage() {
  const { can, modules } = useSession();
  /* Support is a screen both modules share, and Billing is reached from both rails. */
  const link = useModuleLink(modules);
  const queryClient = useQueryClient();
  /* What is actually padlocked right now, so the plan screen answers the question somebody arrives
     at it with — "what have I lost, and what would I get back?" The counts are the server's; this
     screen no more works out which records are shut than any other one does. */
  const gating = useGating();
  /* Null until the settings payload lands, which draws no meter rather than a wrong one. */
  const properties = gating?.limits?.properties ?? null;

  /*
    The way back from the provider.

    `POST /billing/checkout` sets its own callback to `/app/settings/billing?ref=<reference>`, so
    this screen is the return page as well as the shop. The reference is read from the address bar
    rather than kept in state, because the round trip leaves the application entirely — on a phone
    it may even come back in a different tab.
  */
  const [search, setSearch] = useSearchParams();
  const reference = search.get("ref");

  /** The plan being bought, and on what term. Non-null while the purchase dialogue is open. */
  const [buying, setBuying] = useState<{ plan: PlanCode; period: BillingPeriod } | null>(null);
  /** The plan being moved down to. Non-null while the confirmation is open. */
  const [leaving, setLeaving] = useState<PlanCode | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [started, setStarted] = useState<CheckoutStarted | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["billing"],
    queryFn: () => api.get<BillingResponse>("/settings/billing"),
  });

  /*
    What we can take a payment through, asked of the server rather than assumed.

    A provider whose key is missing or no longer works is absent from this list entirely, which is
    the same rule the tenant's pay link follows: offering a way to pay that fails at the moment
    somebody presses it is worse than not offering it. With none of them ready the purchase
    dialogue says so plainly and points at us instead of at a dead button.
  */
  const { data: gateways } = useQuery({
    queryKey: ["platform-gateways"],
    queryFn: () => api.get<{ gateways: PlatformGateway[] }>("/settings/platform-gateways"),
  });
  const providers = gateways?.gateways ?? [];

  /*
    What happened to the payment they have just come back from.

    Asked of the public endpoint, which asks the *provider* — landing on a return URL proves
    nothing, because anybody can visit one with any reference. While it is pending the answer is
    asked for again every few seconds: the webhook and the return trip race each other, and the
    reader should not have to reload a page to find out whether they have paid.
  */
  const charge = useQuery({
    queryKey: ["charge", reference],
    queryFn: () => api.get<ChargeState>(`/public/charges/${encodeURIComponent(reference ?? "")}`),
    enabled: Boolean(reference),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 5_000 : false),
  });

  /*
    The plan moved while the reader was away, so everything that reads a plan is now stale.

    Not only this screen: the rail, the padlocks and the dashboard are all drawn from the plan, and
    an agency that has just paid and still sees its old ceilings has been given every reason to
    doubt that the payment worked.
  */
  const paid = charge.data?.status === "paid";
  useEffect(() => {
    if (!paid) return;
    for (const key of [["billing"], ["settings"], ["me"], ["dashboard"]]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }, [paid, queryClient]);

  /**
   * Moving down, which costs nothing and happens at once.
   *
   * The only plan change this screen makes by itself. An upgrade sent here comes back `402` by
   * design, and would be drawn by `Refusal` rather than as a red box — but it should never get
   * that far, because the button that would have caused it goes to checkout instead.
   */
  const changePlan = useApiMutation({
    mutationFn: (input: { plan: PlanCode; period: BillingPeriod }) =>
      api.post<{ note: string; plan: PlanCode; moved: boolean }>("/settings/billing/plan", input),
    invalidates: [["billing"], ["settings"], ["me"], ["dashboard"]],
    onSuccess: () => setLeaving(null),
  });

  /**
   * Moving up, which is a purchase.
   *
   * On a deployment with real keys the payer leaves for the provider immediately and comes back to
   * this screen with `?ref=`. With no keys there is nowhere to send them, and the dialogue says
   * exactly that rather than redirecting to a page that would have nothing to show — the same
   * decision, for the same reason, as the tenant's pay link.
   */
  const checkout = useApiMutation({
    mutationFn: (input: { provider: string; plan: PlanCode; period: BillingPeriod }) =>
      api.post<CheckoutStarted>("/billing/checkout", input),
    onSuccess: (result) => {
      setStarted(result);
      if (!result.simulated && result.redirectUrl) window.location.href = result.redirectUrl;
    },
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const current = data.plan.code;
  /* The term the agency is actually on, which decides whether buying a year is a move up at all.
     Narrowed through the core guard rather than cast: the column is a string, and an agency with
     no subscription row at all has no term. */
  const storedPeriod = data.subscription?.period;
  const currentPeriod: BillingPeriod = isBillingPeriod(storedPeriod) ? storedPeriod : "monthly";

  const openPurchase = (plan: PlanCode, period: BillingPeriod) => {
    checkout.reset();
    setStarted(null);
    setProvider(providers[0]?.provider ?? null);
    setBuying({ plan, period });
  };

  return (
    <div>
      <PageHeader
        title="Plan and billing"
        back={{ to: "/app/settings", label: "Settings" }}
        subtitle="Priced by managed units. Staff logins are never metered."
        description={"Your plan, what it includes, and how much of it you are using. Priced by managed units — staff logins are never metered."}
      />

      {reference ? (
        <PaymentReturn
          state={charge.data ?? null}
          checking={charge.isFetching}
          planName={PLANS[current].name}
          onCheckAgain={() => void charge.refetch()}
          onDismiss={() => {
            const next = new URLSearchParams(search);
            next.delete("ref");
            setSearch(next, { replace: true });
          }}
        />
      ) : null}

      {changePlan.isSuccess ? (
        <div className="mb-4">
          <Alert tone="info" title="Plan updated.">
            {changePlan.data?.note}
          </Alert>
        </div>
      ) : null}
      {/*
        A refusal that named a plan is drawn as the padlock and the way out of it; everything else
        is the ordinary red box. `Refusal` makes that decision once, so a `402` carrying a gate can
        never be drawn here as a failure — which on the one screen whose job is to sell the product
        would be the most expensive red box in it.
      */}
      <div className="mb-4 empty:mb-0">
        <Refusal error={changePlan.error} message={changePlan.errorMessage} />
      </div>

      <Card className="mb-5">
        <CardTitle
          hint={
            data.plan.trialDaysLeft !== null
              ? `${data.plan.trialDaysLeft} days left of your trial. After that you drop to the free plan — nothing is deleted.`
              : undefined
          }
        >
          You are on {PLANS[current].name}
        </CardTitle>

        <div className="grid gap-5 sm:grid-cols-3">
          {/*
            Properties, first, because it is the ceiling that bites first and the one nobody has
            met before — it did not exist until the free plan grew one. Read from `gating.limits`
            rather than from `usage`, which never carried it: the server counts it beside every
            other shut door, and a second count made here is a second count that can disagree with
            the padlocks on the list.
          */}
          {properties ? (
            <MeterBar
              value={properties.used}
              max={Number.isFinite(properties.limit) ? properties.limit : Math.max(1, properties.used)}
              tone={properties.nearLimit ? "warn" : "brand"}
              label={
                Number.isFinite(properties.limit)
                  ? `${properties.used} of ${pluralise(properties.limit, "property", "properties")}`
                  : `${pluralise(properties.used, "property", "properties")} — unlimited`
              }
            />
          ) : null}
          <MeterBar
            value={data.usage.units.used}
            max={Number.isFinite(data.usage.units.limit) ? data.usage.units.limit : Math.max(1, data.usage.units.used)}
            tone={data.usage.units.nearLimit ? "warn" : "brand"}
            /* `pluralise` rather than a bare number and an "s". A plan whose allowance happens to be
               one read "1 properties" on this screen for as long as it existed, which is the kind
               of mistake that makes a reader doubt the figures beside it. */
            label={
              Number.isFinite(data.usage.units.limit)
                ? `${data.usage.units.used} of ${pluralise(data.usage.units.limit, "unit")}`
                : `${pluralise(data.usage.units.used, "unit")} — unlimited`
            }
          />
          <MeterBar
            value={data.usage.messages.used}
            max={Number.isFinite(data.usage.messages.limit) ? data.usage.messages.limit : Math.max(1, data.usage.messages.used)}
            tone={data.usage.messages.nearLimit ? "warn" : "brand"}
            label={
              Number.isFinite(data.usage.messages.limit)
                ? `${data.usage.messages.used} of ${pluralise(data.usage.messages.limit, "message")} this month`
                : `${pluralise(data.usage.messages.used, "message")} this month — unlimited`
            }
          />
        </div>

        {/*
          What is actually shut, said here rather than left to be discovered a row at a time.

          The sentence this replaces said the opposite — "it never locks the records you already
          have" — and stopped being true the day records above an allowance started carrying a
          padlock. A promise that quietly stops being true is worse than one that was never made,
          so this says what happens instead, and says the part that matters most: nothing is
          deleted and nothing stops being billed.
        */}
        <LockSummary gating={gating} />

        {/*
          What the free plan is, said where somebody is deciding whether to leave it.

          The number is read from the plan table rather than typed, because a figure quoted on a
          billing screen that no longer matches what the API enforces is the worst kind of wrong:
          the agency believes the screen, tries it, and is refused. It is now *pluralised* from the
          table too: it read "up to 1 properties" for as long as the free allowance was one, on the
          one screen in the product whose whole job is to be believed.
        */}
        <p className="mt-2 text-xs text-ink-500">
          The free plan is not a trial. It keeps your properties, units, clients, tenancies, invoices and receipts, it
          sends reminders by hand, and it puts up to {pluralise(limitFor("starter", "listings"), "property", "properties")}{" "}
          on the public marketplace. Every plan above it publishes as many as you like. If you move down, the adverts you
          already have stay up — you simply cannot add another until you are back under{" "}
          {limitFor("starter", "listings")}.
        </p>
      </Card>

      <div className="grid gap-5 lg:grid-cols-4">
        {PLAN_ORDER.map((code) => {
          const plan = PLANS[code];
          const isCurrent = code === current;
          /*
            Which of the three this card is, decided by the same function the API decides it with.
            The term compared against is the monthly one, because the card is about the plan; the
            choice between paying by the month and by the year is made inside the purchase
            dialogue, where the two prices can be put side by side.
          */
          const direction = planMove(current, code, currentPeriod, "monthly");
          /* A customer already on this plan, paying monthly, can still buy the year — which
             `planMove` calls a move up, because it is one, and which therefore goes through the
             till like any other. */
          const yearlyIsAStepUp =
            isCurrent && planMove(current, code, currentPeriod, "yearly") === "up";

          return (
            <Card key={code} className={`flex flex-col ${isCurrent ? "border-brand-600 ring-1 ring-brand-600" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                {isCurrent ? <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs text-white">Current</span> : null}
              </div>
              <p className="mt-1 text-sm text-ink-600">{plan.blurb}</p>
              <p className="tabular mt-3 text-xl font-semibold">{formatPlanPrice(code)}</p>
              {plan.monthly > 0 ? (
                <p className="text-xs text-ink-500">or {formatPlanPrice(code, "yearly")} — two months free</p>
              ) : null}

              <ul className="mt-3 flex-1 space-y-1 text-sm text-ink-700">
                {(Object.keys(FEATURE_LABELS) as Feature[])
                  .filter((feature) => plan.features.includes(feature))
                  .slice(0, 7)
                  .map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <span className="text-brand-600">✓</span>
                      <span>{FEATURE_LABELS[feature]}</span>
                    </li>
                  ))}
              </ul>

              {/*
                Enterprise, which has no price and therefore no button that could work. Both
                endpoints refuse it in the same words; offering a control that is certain to be
                refused would be the product wasting the reader's time to make a point. Drawn for
                everybody who can see this screen, because asking to talk to us is not a purchase.
              */}
              {direction === "quote" ? (
                <Link
                  to={link("/app/support")}
                  className="btn btn-secondary mt-4 w-full"
                  data-plan-action="quote"
                  data-plan-code={code}
                >
                  Get in touch
                </Link>
              ) : null}

              {/*
                And everything that is a purchase or a departure, offered only to somebody who may
                actually make one. Absent rather than padlocked for anybody else — ADR-0114.
              */}
              {can("org.billing") && direction === "up" ? (
                <Button
                  className="mt-4 w-full"
                  variant={plan.featured ? "primary" : "secondary"}
                  data-plan-action="up"
                  data-plan-code={code}
                  onClick={() => openPurchase(code, "monthly")}
                >
                  Move up to {plan.name}
                </Button>
              ) : null}

              {can("org.billing") && direction === "down" ? (
                <Button
                  className="mt-4 w-full"
                  variant="secondary"
                  data-plan-action="down"
                  data-plan-code={code}
                  onClick={() => {
                    changePlan.reset();
                    setLeaving(code);
                  }}
                >
                  Move down to {plan.name}
                </Button>
              ) : null}

              {can("org.billing") && yearlyIsAStepUp ? (
                <Button
                  className="mt-4 w-full"
                  variant="secondary"
                  data-plan-action="term"
                  data-plan-code={code}
                  onClick={() => openPurchase(code, "yearly")}
                >
                  Pay for a year instead
                </Button>
              ) : null}
            </Card>
          );
        })}
      </div>

      <Card className="mt-5">
        <CardTitle>How a plan changes</CardTitle>
        {/*
          The sentence this replaces said "nothing is charged and no card is stored", which was
          true of the screen it was written for and is the opposite of true now. Left standing it
          would be the product telling a customer their payment did not matter at the moment it
          did.
        */}
        <p className="text-sm text-ink-600">
          Moving up is a payment. The plan changes the moment the payment is confirmed, and not
          before — nothing changes if a payment is abandoned part way. Moving down, and returning to
          the free plan, are free and take effect at once.
        </p>
        <p className="mt-2 text-xs text-ink-500">
          Rent paid by your tenants is entirely separate and always settles into your own account — RentorNG never holds
          client money.
        </p>
      </Card>

      {buying ? (
        <PurchaseDialogue
          plan={buying.plan}
          period={buying.period}
          onPeriod={(period) => setBuying({ plan: buying.plan, period })}
          providers={providers}
          provider={provider}
          onProvider={setProvider}
          started={started}
          pending={checkout.isPending}
          error={checkout.error}
          errorMessage={checkout.errorMessage}
          onStart={() => {
            if (!provider) return;
            checkout.mutate({ provider, plan: buying.plan, period: buying.period });
          }}
          onClose={() => {
            setBuying(null);
            setStarted(null);
          }}
        />
      ) : null}

      {leaving ? (
        <DepartureDialogue
          from={current}
          to={leaving}
          pending={changePlan.isPending}
          error={changePlan.error}
          errorMessage={changePlan.errorMessage}
          onConfirm={() => changePlan.mutate({ plan: leaving, period: currentPeriod })}
          onClose={() => setLeaving(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The payment they have just come back from, answered by the provider rather than by the address.
 *
 * Three answers and three tones, and only one of them is a failure. A payment still pending is not
 * a problem — the webhook and the return trip race each other and either may win — so it takes the
 * waiting tone and offers to ask again, rather than telling somebody who has just paid us that
 * something has gone wrong.
 */
function PaymentReturn({
  state,
  checking,
  planName,
  onCheckAgain,
  onDismiss,
}: {
  state: ChargeState | null;
  checking: boolean;
  planName: string;
  onCheckAgain: () => void;
  onDismiss: () => void;
}) {
  if (!state) {
    return (
      <div className="mb-4" data-charge-return="checking">
        <Alert tone="info" title="Checking your payment.">
          One moment while the provider is asked what happened.
        </Alert>
      </div>
    );
  }

  const tone = state.status === "paid" ? "success" : state.status === "pending" ? "warn" : "danger";
  const title =
    state.status === "paid"
      ? "Payment confirmed."
      : state.status === "pending"
        ? "The payment has not been confirmed yet."
        : "The payment did not go through.";

  return (
    <div className="mb-4" data-charge-return={state.status}>
      <Alert tone={tone} title={title}>
        <p>
          {state.status === "paid"
            ? `Your agency is on ${planName}. The receipt has been sent to the address the payment was made from.`
            : state.status === "pending"
              ? "A payment can take a short while to reach us. Your plan is unchanged until it does, and nothing has been taken twice."
              : "Nothing has been taken and your plan is unchanged. You can start the payment again, or use a different provider."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {state.status === "pending" ? (
            <Button variant="secondary" size="sm" disabled={checking} onClick={onCheckAgain} data-charge-recheck>
              {checking ? "Checking…" : "Check again"}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onDismiss} data-charge-dismiss>
            Dismiss
          </Button>
        </div>
      </Alert>
    </div>
  );
}

/**
 * Buying a plan: the term, the provider, and then the provider's own page.
 *
 * Deliberately a dialogue rather than a button. Two decisions have to be made before a charge can
 * be opened at all — how long a term, and through whom — and a screen that made either of them on
 * the customer's behalf would be choosing how much to take from them.
 *
 * **Nothing here says the plan has moved**, because nothing here moves it. The most this dialogue
 * ever reports is that a payment was started; the answer arrives on the return trip.
 */
function PurchaseDialogue({
  plan,
  period,
  onPeriod,
  providers,
  provider,
  onProvider,
  started,
  pending,
  error,
  errorMessage,
  onStart,
  onClose,
}: {
  plan: PlanCode;
  period: BillingPeriod;
  onPeriod: (period: BillingPeriod) => void;
  providers: PlatformGateway[];
  provider: string | null;
  onProvider: (provider: string) => void;
  started: CheckoutStarted | null;
  pending: boolean;
  error: unknown;
  errorMessage: string | null;
  onStart: () => void;
  onClose: () => void;
}) {
  const named = PLANS[plan];
  const chosen = providers.find((entry) => entry.provider === provider) ?? null;
  const { modules } = useSession();
  const link = useModuleLink(modules);

  return (
    <Modal open onClose={onClose} title={`Move up to ${named.name}`}>
      <div data-purchase-dialogue={plan}>
        {/*
          Started, with nowhere to send them: no provider keys are configured on this installation,
          so a charge was written and nothing was taken. Said plainly rather than smoothed over —
          a screen that claims to be redirecting to a payment page that does not exist is the
          payment equivalent of saying an email was sent when it was not.
        */}
        {started?.simulated ? (
          <div data-checkout-simulated={started.reference}>
            <Alert tone="warn" title="No payment was taken.">
              Online payment is not connected to a provider here, so there was nowhere to send you
              and your plan has not moved. The attempt was recorded as {started.reference}.
            </Alert>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : providers.length === 0 ? (
          <div data-checkout-unavailable>
            <p className="text-sm text-ink-700">
              There is no way to pay online at the moment, so this plan cannot be bought from here.
              Get in touch and we will arrange it with you directly.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to={link("/app/support")} className="btn btn-primary">
                Get in touch
              </Link>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-ink-700">
              {named.blurb} The plan changes the moment the payment is confirmed, and everything you
              already have stays exactly as it is.
            </p>

            <fieldset className="mt-4">
              <legend className="eyebrow mb-2">Term</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {(["monthly", "yearly"] as BillingPeriod[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onPeriod(option)}
                    data-checkout-period={option}
                    aria-pressed={period === option}
                    className={`rounded-[var(--radius-surface)] border px-3 py-2 text-left ${
                      period === option ? "border-brand-600 bg-brand-50" : "border-ink-200 bg-white hover:border-ink-300"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-ink-900">
                      {formatPlanPrice(plan, option)}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {option === "monthly" ? "Paid every month" : "Paid once a year — two months free"}
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-4">
              <legend className="eyebrow mb-2">Pay through</legend>
              <div className="grid gap-2">
                {providers.map((entry) => (
                  <button
                    key={entry.provider}
                    type="button"
                    onClick={() => onProvider(entry.provider)}
                    data-checkout-provider={entry.provider}
                    aria-pressed={provider === entry.provider}
                    className={`rounded-[var(--radius-surface)] border px-3 py-2 text-left text-sm font-medium ${
                      provider === entry.provider
                        ? "border-brand-600 bg-brand-50 text-brand-800"
                        : "border-ink-200 bg-white text-ink-800 hover:border-ink-300"
                    }`}
                  >
                    {entry.name}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="mt-4 empty:mt-0">
              <Refusal error={error} message={errorMessage} />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button disabled={pending || !chosen} onClick={onStart} data-checkout-start>
                {pending ? "Starting…" : chosen ? `Continue to ${chosen.name}` : "Continue"}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
            <p className="mt-3 text-xs text-ink-500">
              You will be taken to the provider to pay. Nothing changes on your account until they
              confirm the payment, and you are brought straight back here afterwards.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Moving down, and what it costs — asked once, in full, before anything happens.
 *
 * The confirmation this replaces was a `window.confirm` naming automatic reminders. It was right
 * about the features and silent about the thing an agency actually fears, which is what happens to
 * the records it has already entered. So this says the whole of it: what stops working, what the
 * new allowances are, and — the sentence that matters most — that **nothing is deleted and nothing
 * stops being billed.**
 *
 * **It counts nothing.** Which records would close is the server's answer (`gating.locks`), and a
 * count worked out here would be a second count able to disagree with the padlocks on the list.
 * The allowances come from the plan table; the rule is stated in words.
 */
function DepartureDialogue({
  from,
  to,
  pending,
  error,
  errorMessage,
  onConfirm,
  onClose,
}: {
  from: PlanCode;
  to: PlanCode;
  pending: boolean;
  error: unknown;
  errorMessage: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const target = PLANS[to];
  /* Membership of a feature list, not a comparison of plan codes: what is in the one and not in
     the other, in the product's own words for each. */
  const losing = (Object.keys(FEATURE_LABELS) as Feature[]).filter(
    (feature) => PLANS[from].features.includes(feature) && !target.features.includes(feature),
  );

  const allowance = (limit: "properties" | "units", singular: string, plural: string) => {
    const max = limitFor(to, limit);
    return Number.isFinite(max) ? pluralise(max, singular, plural) : `unlimited ${plural}`;
  };

  return (
    <Modal open onClose={onClose} title={`Move down to ${target.name}`}>
      <div data-departure-dialogue={to}>
        <p className="text-sm text-ink-700">
          {target.name} allows {allowance("properties", "property", "properties")} and{" "}
          {allowance("units", "unit", "units")}. The change takes effect at once.
        </p>

        {losing.length ? (
          <div className="mt-4">
            <p className="eyebrow mb-2">What stops working</p>
            <ul className="space-y-1 text-sm text-ink-700" data-departure-losing>
              {losing.map((feature) => (
                <li key={feature} className="flex gap-2">
                  <span className="text-ink-400">·</span>
                  <span>{FEATURE_LABELS[feature]}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mt-4 text-sm text-ink-700">
            Everything included in your current plan is included in {target.name} as well.
          </p>
        )}

        <div className="mt-4 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-brand-800">
            <IconLock size={16} />
            What happens to your records
          </p>
          <p className="mt-1.5 text-sm text-brand-800">
            Nothing is deleted. Anything over the new allowance is kept and padlocked — the most
            recently added stay open and the older ones close — and every one of them goes on being
            billed. Invoices still raise, reminders still send, payments still post, and adverts
            already on the marketplace stay up.
          </p>
        </div>

        <div className="mt-4 empty:mt-0">
          <Refusal error={error} message={errorMessage} />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button disabled={pending} onClick={onConfirm} data-departure-confirm>
            {pending ? "Moving…" : `Move down to ${target.name}`}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * How many records are open, and how many are behind a padlock.
 *
 * Two lines at most, and only when there is something to say. An agency under its allowance sees
 * the reassurance and nothing else; an agency over it sees exactly what it would get back, which
 * is the only figure that makes an upgrade an argument rather than an appeal.
 *
 * **The counts are the server's** — `gating.locks` — for the same reason every other padlock in
 * the product is: a number worked out in the browser is a number that can disagree with the one
 * the list is drawn from, and this is the screen where being believed matters most.
 */
function LockSummary({ gating }: { gating: ReturnType<typeof useGating> }) {
  const shut = (["properties", "units"] as const)
    .map((kind) => ({ kind, view: gating?.locks?.[kind] }))
    .filter((entry) => entry.view?.over);

  if (shut.length === 0) {
    return (
      <p className="mt-4 text-xs text-ink-500">
        Reaching a limit stops you adding more; everything already recorded stays, goes on being
        billed, and stays open. Above the allowance the most recently added stay open and the older
        ones are padlocked — nothing is ever deleted, and export always works on a plan that
        includes it.
      </p>
    );
  }

  return (
    <div className="mt-4 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-brand-800">
        <IconLock size={16} />
        What moving up would open again
      </p>
      <ul className="mt-1.5 space-y-0.5 text-sm text-brand-800">
        {shut.map(({ kind, view }) => (
          <li key={kind}>
            {pluralise(view?.locked.length ?? 0, kind === "properties" ? "property" : "unit", kind === "properties" ? "properties" : "units")}{" "}
            padlocked, {view?.allowance} of {view?.total} open.
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-xs text-brand-700">
        Nothing is deleted and nothing stops being billed. Invoices still raise, reminders still
        send, payments still post — what is padlocked is the screen, and the most recently added
        stay open.
      </p>
    </div>
  );
}
